import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BrowserRouter as Router,
  Link,
  Route,
  Switch,
  useParams,
} from "react-router-dom";
import axios from "axios";
import {
  FaUser,
  FaIdCard,
  FaGraduationCap,
  FaPhone,
  FaEnvelope,
  FaBuilding,
  FaEdit,
  FaCamera,
  FaRedo,
  FaCloudUploadAlt,
  FaPlus,
  FaArrowLeft,
  FaUsers,
  FaTimes,
  FaCheck,
  FaPencilAlt,
  FaCalendarAlt,
  FaVenusMars,
  FaPray,
  FaGlobe,
  FaFingerprint,
  FaUserTag,
  FaSignInAlt,
  FaMapMarkerAlt,
  FaRegClock,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import EditStaff from "./EditStaff";
import AddQualification from "./AddQualification";
import EditQualification from "./EditQualification";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

function fmt(v) {
  if (v == null || String(v).trim() === "") return "—";
  return String(v);
}

function staffInitials(name) {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

function DetailField({ label, children, className = "", icon: Icon, tone = "" }) {
  return (
    <div
      className={`sp-field${tone ? ` sp-field--${tone}` : ""}${
        className ? ` ${className}` : ""
      }`}
    >
      <span className="sp-field__label">
        {Icon ? (
          <span className="sp-field__icon" aria-hidden>
            <Icon />
          </span>
        ) : null}
        {label}
      </span>
      <span className="sp-field__value">{children}</span>
    </div>
  );
}

function SectionCard({ icon: Icon, title, action, children, className = "" }) {
  return (
    <section className={`sp-section${className ? ` ${className}` : ""}`}>
      <div className="sp-section__head">
        <span className="sp-section__icon" aria-hidden>
          <Icon />
        </span>
        <h2 className="sp-section__title">{title}</h2>
        {action ? <div className="sp-section__action">{action}</div> : null}
      </div>
      <div className="sp-section__body">{children}</div>
    </section>
  );
}

function canvasToJpegFile(canvas, baseName) {
  const finish = (blob) => {
    if (!blob || blob.size === 0) {
      throw new Error("Could not encode image");
    }
    return new File([blob], `${baseName}_rotated.jpg`, {
      type: "image/jpeg",
    });
  };

  return new Promise((resolve, reject) => {
    const fallback = () => {
      try {
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        const m = /^data:image\/jpeg;base64,(.+)$/.exec(dataUrl);
        if (!m) {
          reject(new Error("Could not encode image"));
          return;
        }
        const bin = atob(m[1]);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        resolve(finish(new Blob([arr], { type: "image/jpeg" })));
      } catch (err) {
        reject(err);
      }
    };

    if (typeof canvas.toBlob === "function") {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            try {
              resolve(finish(blob));
            } catch (e) {
              reject(e);
            }
          } else {
            fallback();
          }
        },
        "image/jpeg",
        0.92
      );
    } else {
      fallback();
    }
  });
}

function rotateImageFile(file, degrees) {
  const normalized = ((degrees % 360) + 360) % 360;
  if (normalized === 0) return Promise.resolve(file);

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = async () => {
      URL.revokeObjectURL(url);
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) {
        reject(new Error("Invalid image dimensions"));
        return;
      }
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (normalized === 90 || normalized === 270) {
        canvas.width = h;
        canvas.height = w;
      } else {
        canvas.width = w;
        canvas.height = h;
      }
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((normalized * Math.PI) / 180);
      ctx.drawImage(img, -w / 2, -h / 2);

      const base =
        file.name.replace(/\.[^.]+$/, "").replace(/_rotated$/, "") || "photo";
      try {
        const outFile = await canvasToJpegFile(canvas, base);
        resolve(outFile);
      } catch (e) {
        reject(e instanceof Error ? e : new Error("Could not encode image"));
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
}

function resolveImageUrl(path) {
  const p = String(path || "").trim();
  if (!p) throw new Error("No image URL");
  if (/^https?:\/\//i.test(p)) return p;
  const base = (process.env.REACT_APP_SERVER_NAME || "").replace(/\/$/, "");
  if (base && p.startsWith("/")) {
    return `${base}${p}`;
  }
  try {
    return new URL(p, window.location.origin).href;
  } catch {
    return p;
  }
}

async function rotateImageFromUrl(imageUrl, degrees) {
  const normalized = ((degrees % 360) + 360) % 360;
  if (normalized === 0) throw new Error("No rotation");

  const tryAxiosBlob = async () => {
    const { data } = await axios.get(imageUrl, {
      responseType: "blob",
      withCredentials: true,
    });
    if (!(data instanceof Blob) || data.size === 0) {
      throw new Error("Empty image");
    }
    return data;
  };

  const tryFetchBlob = async () => {
    let res = await fetch(imageUrl, { credentials: "include" });
    if (!res.ok) {
      res = await fetch(imageUrl, { mode: "cors", credentials: "omit" });
    }
    if (!res.ok) throw new Error("Could not load photo");
    return res.blob();
  };

  const tryCanvasFromImage = (useCors) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      if (useCors) img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          canvas.getContext("2d").drawImage(img, 0, 0);
          canvas.toBlob((b) => {
            if (b) resolve(b);
            else reject(new Error("Could not read photo"));
          }, "image/png");
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = () => reject(new Error("Image load failed"));
      img.src = imageUrl;
    });

  let blob;
  try {
    blob = await tryAxiosBlob();
  } catch {
    try {
      blob = await tryFetchBlob();
    } catch (primary) {
      try {
        blob = await tryCanvasFromImage(false);
      } catch {
        try {
          blob = await tryCanvasFromImage(true);
        } catch {
          throw primary instanceof Error ? primary : new Error("Load failed");
        }
      }
    }
  }

  const mime =
    blob.type && blob.type.startsWith("image/") ? blob.type : "image/jpeg";
  const ext = mime.includes("png")
    ? "png"
    : mime.includes("webp")
      ? "webp"
      : "jpg";
  const file = new File([blob], `staff.${ext}`, { type: mime });
  return rotateImageFile(file, degrees);
}

function parseUploadApiResponse(data) {
  if (data == null) return null;
  if (typeof data === "object" && !Array.isArray(data)) return data;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

const StaffProfile = () => {
  const { Staff_id } = useParams();
  const [bootLoading, setBootLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [staffProfile, setStaffProfile] = useState(null);
  const [staffQualifications, setStaffQualifications] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewURL, setPreviewURL] = useState(null);
  const [photoRotationDeg, setPhotoRotationDeg] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [photoRevision, setPhotoRevision] = useState(0);
  const [showAddQualification, setShowAddQualification] = useState(false);
  const [qDegree, setQDegree] = useState("");
  const [qUniversity, setQUniversity] = useState("");
  const [qYear, setQYear] = useState("");
  const [qPercentage, setQPercentage] = useState("");

  useEffect(() => {
    setSelectedFile(null);
    setPreviewURL(null);
    setPhotoRotationDeg(0);
    setRefreshKey(0);
    setPhotoRevision(0);
  }, [Staff_id]);

  const fetchStaffProfile = useCallback(async () => {
    setBootLoading(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();

    try {
      const response = await axios.get(`${API_BASE()}getStaffDetails.php`, {
        params: { staff_id: Staff_id, school_code: SCHOOL_CODE, _t: refreshKey },
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      });
      if (response.data.api_status === "OK") {
        const item = response.data.data?.[0] || null;
        setStaffProfile(item);
        setStaffQualifications(item?.qualification || []);
      } else {
        setStaffProfile(null);
        setStaffQualifications([]);
        myToast.notify_warning(
          response.data.api_remark || "Could not load staff profile."
        );
      }
    } catch (error) {
      setStaffProfile(null);
      setStaffQualifications([]);
      console.error("Error fetching staff details:", error);
      myToast.notify_warning("Network error loading staff profile.");
    } finally {
      setBootLoading(false);
    }
  }, [Staff_id, refreshKey]);

  useEffect(() => {
    fetchStaffProfile();
  }, [fetchStaffProfile]);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) {
      myToast.notify_warning("Please select a file.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      myToast.notify_warning("Please select a valid image file.");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedFile(file);
      setPreviewURL(reader.result);
      setPhotoRotationDeg(0);
    };
    reader.onerror = () => myToast.notify_warning("Failed to read file.");
    reader.readAsDataURL(file);
  }, []);

  const handleUpload = useCallback(async () => {
    const school_code = SessionManager.getSchoolCode();
    const rot = ((photoRotationDeg % 360) + 360) % 360;
    const staff_image_path = staffProfile?.staff_image_path?.trim();
    const hasUploadedPhoto = Boolean(staff_image_path);
    const uploadNewFile = Boolean(selectedFile);
    const uploadRotatedExisting =
      !uploadNewFile && hasUploadedPhoto && rot !== 0;

    if (!uploadNewFile && !uploadRotatedExisting) {
      if (!hasUploadedPhoto) {
        myToast.notify_warning("Please select an image first.");
      } else {
        myToast.notify_warning(
          "Use Rotate to change orientation, then click Upload."
        );
      }
      return;
    }

    if (!Staff_id || !school_code) {
      myToast.notify_warning("Missing staff ID or school code");
      return;
    }

    setBusy(true);
    let fileToUpload;
    try {
      if (uploadNewFile) {
        fileToUpload =
          rot !== 0 ? await rotateImageFile(selectedFile, rot) : selectedFile;
      } else {
        const absUrl = resolveImageUrl(staff_image_path);
        const fetchUrl = `${absUrl}${absUrl.includes("?") ? "&" : "?"}v=${refreshKey}`;
        fileToUpload = await rotateImageFromUrl(fetchUrl, rot);
      }
    } catch (e) {
      console.error(e);
      setBusy(false);
      const hint =
        e instanceof Error && e.message
          ? e.message
          : "Could not prepare the image.";
      myToast.notify_warning(
        `${hint} You can also choose the photo again and upload.`
      );
      return;
    }

    const formData = new FormData();
    formData.append("image", fileToUpload);
    formData.append("staff_id", Staff_id);
    formData.append("school_code", school_code);

    try {
      const response = await axios.post(
        `${API_BASE()}uploadStaffProfilePic.php`,
        formData
      );
      const payload = parseUploadApiResponse(response.data);
      if (payload?.api_status === "OK") {
        myToast.notify_success(
          payload.api_remark || "Image uploaded successfully!"
        );
        setSelectedFile(null);
        setPreviewURL(null);
        setPhotoRotationDeg(0);
        setPhotoRevision((n) => n + 1);
        setRefreshKey((k) => k + 1);
      } else {
        myToast.notify_warning(payload?.api_remark || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      const errPayload = parseUploadApiResponse(error.response?.data);
      myToast.notify_warning(
        errPayload?.api_remark ||
          errPayload?.message ||
          "Image upload failed."
      );
    } finally {
      setBusy(false);
    }
  }, [
    Staff_id,
    photoRotationDeg,
    refreshKey,
    selectedFile,
    staffProfile,
  ]);

  const resetQualificationForm = useCallback(() => {
    setQDegree("");
    setQUniversity("");
    setQYear("");
    setQPercentage("");
  }, []);

  const openAddQualification = useCallback(() => {
    resetQualificationForm();
    setShowAddQualification(true);
  }, [resetQualificationForm]);

  const closeAddQualification = useCallback(() => {
    setShowAddQualification(false);
  }, []);

  const submitQualification = useCallback(async () => {
    const degree = String(qDegree || "").trim();
    const university = String(qUniversity || "").trim();
    const passing_year = String(qYear || "").trim();
    const percentage = String(qPercentage || "").trim();

    if (!degree) return myToast.notify_warning("Please enter Degree/Diploma");
    if (!university)
      return myToast.notify_warning("Please enter University/Institute");
    if (!passing_year)
      return myToast.notify_warning("Please enter Passing Year");
    if (!percentage) return myToast.notify_warning("Please enter Percentage");

    const school_code = SessionManager.getSchoolCode();
    if (!school_code || !Staff_id) {
      myToast.notify_warning("Missing staff ID or school code");
      return;
    }

    const existing = Array.isArray(staffQualifications)
      ? staffQualifications
      : [];
    const normalizedExisting = existing.map((q) => ({
      degree: q?.degree ?? q?.staff_degree ?? "",
      university: q?.university ?? q?.staff_university ?? "",
      passing_year: q?.passing_year ?? q?.staff_passing_year ?? "",
      percentage: q?.percentage ?? q?.staff_percentage ?? "",
    }));

    const qualification = JSON.stringify([
      ...normalizedExisting,
      { degree, university, passing_year, percentage },
    ]);

    setBusy(true);
    try {
      const response = await axios.get(
        `${API_BASE()}AddStaffNewQualification.php`,
        {
          params: {
            school_code,
            staff_id: Staff_id,
            qualification,
          },
        }
      );

      if (response.data.api_status === "OK") {
        myToast.notify_success("Qualification added");
        closeAddQualification();
        resetQualificationForm();
        setRefreshKey((k) => k + 1);
      } else {
        myToast.notify_warning(response.data.api_remark || "Add failed");
      }
    } catch (e) {
      console.error(e);
      myToast.notify_error("Error adding qualification");
    } finally {
      setBusy(false);
    }
  }, [
    Staff_id,
    closeAddQualification,
    qDegree,
    qPercentage,
    qUniversity,
    qYear,
    resetQualificationForm,
    staffQualifications,
  ]);

  const hasUploadedPhoto = Boolean(staffProfile?.staff_image_path?.trim());
  const rotationApplied = ((photoRotationDeg % 360) + 360) % 360 !== 0;
  const canRotatePhoto = Boolean(selectedFile || hasUploadedPhoto);
  const canUploadPhoto =
    Boolean(selectedFile) || (hasUploadedPhoto && rotationApplied);

  const dynamicFields = useMemo(() => {
    if (!staffProfile) return [];
    const exclude = new Set([
      "qualification",
      "staff_image_path",
      "staff_img_path",
      "school_code",
      "password",
      "staff_name",
      "staff_id",
      "staff_phone",
      "staff_email",
      "aadhar_no",
      "staff_dob",
      "staff_gender",
      "staff_address",
      "staff_role",
      "login_type",
      "department_name",
      "department_id",
      "joining_date",
      "staff_religion",
      "nationality",
      "registration_date",
      "status",
    ]);
    return Object.entries(staffProfile)
      .filter(([k, v]) => !exclude.has(k))
      .filter(
        ([, v]) =>
          v == null || ["string", "number", "boolean"].includes(typeof v)
      )
      .map(([k, v]) => ({
        key: k,
        label: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        value: v,
      }));
  }, [staffProfile]);

  if (bootLoading) return <MyLoader />;

  const profileView = (
    <div
      className="content payroll-page sm-page sp-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Staff Profile</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaUser />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Staff Profile</h1>
              <p className="sm-hero__sub">
                View details, photo, and qualifications
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <Link to="/AdminMain/StaffList/MDL012" className="sp-hero-link">
              <FaArrowLeft aria-hidden /> Staff List
            </Link>
            <Link to="/AdminMain/StaffManagement" className="sp-hero-link">
              <FaUsers aria-hidden /> Staff Hub
            </Link>
            {staffProfile ? (
              <Link
                to={{
                  pathname: "/AdminMain/EditStaff",
                  state: { staffData: staffProfile },
                }}
                className="sp-hero-link sp-hero-link--solid"
              >
                <FaEdit aria-hidden /> Edit
              </Link>
            ) : null}
          </div>
        </header>

        {!staffProfile ? (
          <div className="sp-empty">
            <div className="sp-empty__circle" aria-hidden>
              <FaUser />
            </div>
            <p className="sp-empty__title">No staff found</p>
            <p className="sp-empty__hint">
              No record for this staff ID. Return to the list and try again.
            </p>
            <Link to="/AdminMain/StaffList/MDL012" className="sp-btn sp-btn--primary">
              <FaArrowLeft aria-hidden /> Back to Staff List
            </Link>
          </div>
        ) : (
          <div className="sp-body">
            <div className="sp-profile-card">
              <div className="sp-photo-block">
                <div className="sp-avatar-wrap">
                  {previewURL || staffProfile.staff_image_path ? (
                    <img
                      key={`avatar-${refreshKey}-${photoRevision}`}
                      className="sp-avatar"
                      style={
                        rotationApplied && (selectedFile || hasUploadedPhoto)
                          ? { transform: `rotate(${photoRotationDeg}deg)` }
                          : undefined
                      }
                      src={
                        previewURL ||
                        `${staffProfile.staff_image_path}${
                          staffProfile.staff_image_path.includes("?")
                            ? "&"
                            : "?"
                        }v=${refreshKey}&r=${photoRevision}`
                      }
                      alt=""
                    />
                  ) : (
                    <div className="sp-avatar sp-avatar--initials">
                      {staffInitials(staffProfile.staff_name)}
                    </div>
                  )}
                </div>

                <div className="sp-upload-bar">
                  <label className="sp-file-label">
                    <FaCamera aria-hidden />
                    Choose
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept="image/*"
                      hidden
                    />
                  </label>
                  <button
                    type="button"
                    className="sp-btn sp-btn--ghost"
                    onClick={() => setPhotoRotationDeg((d) => (d + 90) % 360)}
                    disabled={!canRotatePhoto || busy}
                    title="Rotate preview 90° clockwise"
                    aria-label="Rotate image 90 degrees clockwise"
                  >
                    <FaRedo aria-hidden />
                    Rotate
                  </button>
                  <button
                    type="button"
                    className="sp-btn sp-btn--primary"
                    onClick={handleUpload}
                    disabled={!canUploadPhoto || busy}
                  >
                    <FaCloudUploadAlt aria-hidden />
                    {busy ? "Saving…" : "Upload"}
                  </button>
                </div>
              </div>

              <div className="sp-profile-main min-w-0">
                <h2 className="sp-profile-name">
                  {fmt(staffProfile.staff_name)}
                </h2>
                <p className="sp-profile-sub">
                  {[staffProfile.staff_role, staffProfile.login_type]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </p>
                <div className="sp-chip-row">
                  <span className="sp-chip">
                    ID: {fmt(staffProfile.staff_id || Staff_id)}
                  </span>
                  {staffProfile.department_name ? (
                    <span className="sp-chip sp-chip--dept">
                      <FaBuilding aria-hidden />
                      {staffProfile.department_name}
                    </span>
                  ) : null}
                  {staffProfile.staff_gender ? (
                    <span className="sp-chip">{fmt(staffProfile.staff_gender)}</span>
                  ) : null}
                  {staffProfile.status ? (
                    <span
                      className={`sp-chip sp-chip--status${
                        String(staffProfile.status).toLowerCase() === "active"
                          ? " is-active"
                          : ""
                      }`}
                    >
                      {staffProfile.status}
                    </span>
                  ) : null}
                  {staffProfile.joining_date ? (
                    <span className="sp-chip">
                      Joined: {fmt(staffProfile.joining_date)}
                    </span>
                  ) : null}
                </div>

                <div className="sp-contact-row">
                  {staffProfile.staff_phone ? (
                    <span className="sp-contact-item">
                      <FaPhone aria-hidden />
                      {staffProfile.staff_phone}
                    </span>
                  ) : null}
                  {staffProfile.staff_email ? (
                    <span className="sp-contact-item">
                      <FaEnvelope aria-hidden />
                      {staffProfile.staff_email}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <SectionCard
              icon={FaIdCard}
              title="Personal Information"
              className="sp-section--info"
            >
              <div className="sp-info">
                <div className="sp-grid sp-grid--info">
                  <DetailField label="Phone" icon={FaPhone}>
                    {fmt(staffProfile.staff_phone)}
                  </DetailField>
                  <DetailField label="Email" icon={FaEnvelope}>
                    {fmt(staffProfile.staff_email)}
                  </DetailField>
                  <DetailField label="Aadhaar no." icon={FaFingerprint}>
                    {fmt(staffProfile.aadhar_no)}
                  </DetailField>
                  <DetailField label="Date of Birth" icon={FaCalendarAlt}>
                    {fmt(staffProfile.staff_dob)}
                  </DetailField>
                  <DetailField label="Gender" icon={FaVenusMars}>
                    {fmt(staffProfile.staff_gender)}
                  </DetailField>
                  <DetailField label="Religion" icon={FaPray}>
                    {fmt(staffProfile.staff_religion)}
                  </DetailField>
                  <DetailField label="Nationality" icon={FaGlobe}>
                    {fmt(staffProfile.nationality)}
                  </DetailField>
                  <DetailField label="Department" icon={FaBuilding}>
                    {fmt(staffProfile.department_name)}
                  </DetailField>
                  <DetailField label="Role" icon={FaUserTag}>
                    {fmt(staffProfile.staff_role)}
                  </DetailField>
                  <DetailField label="Login Type" icon={FaSignInAlt}>
                    {fmt(staffProfile.login_type)}
                  </DetailField>
                  <DetailField label="Date of Joining" icon={FaCalendarAlt}>
                    {fmt(staffProfile.joining_date)}
                  </DetailField>
                  <DetailField label="Registration Date" icon={FaRegClock}>
                    {fmt(staffProfile.registration_date)}
                  </DetailField>
                  <DetailField
                    label="Address"
                    icon={FaMapMarkerAlt}
                    className="sp-field--full"
                  >
                    {fmt(staffProfile.staff_address)}
                  </DetailField>
                </div>
              </div>
            </SectionCard>

            {dynamicFields.length > 0 ? (
              <SectionCard icon={FaIdCard} title="Additional Details">
                <div className="sp-grid">
                  {dynamicFields.map((f) => (
                    <DetailField key={f.key} label={f.label}>
                      {fmt(f.value)}
                    </DetailField>
                  ))}
                </div>
              </SectionCard>
            ) : null}

            <SectionCard
              icon={FaGraduationCap}
              title="Qualifications"
              action={
                <button
                  type="button"
                  className="sp-btn sp-btn--ghost sp-btn--sm"
                  onClick={openAddQualification}
                >
                  <FaPlus aria-hidden />
                  Add
                </button>
              }
            >
              {staffQualifications?.length ? (
                <div className="sp-qual-list">
                  <div className="sp-qual-head" aria-hidden>
                    <span>Degree</span>
                    <span>University</span>
                    <span>Year</span>
                    <span>%</span>
                    <span />
                  </div>
                  {staffQualifications.map((q, idx) => (
                    <div className="sp-qual-row" key={q.id || idx}>
                      <span
                        className="sp-qual-row__cell"
                        title={fmt(q.staff_degree ?? q.degree)}
                      >
                        {fmt(q.staff_degree ?? q.degree)}
                      </span>
                      <span
                        className="sp-qual-row__cell"
                        title={fmt(q.staff_university ?? q.university)}
                      >
                        {fmt(q.staff_university ?? q.university)}
                      </span>
                      <span className="sp-qual-row__cell sp-qual-row__cell--num">
                        {fmt(q.staff_passing_year ?? q.passing_year)}
                      </span>
                      <span className="sp-qual-row__cell sp-qual-row__cell--num">
                        {fmt(q.staff_percentage ?? q.percentage)}
                      </span>
                      <div className="sp-qual-row__actions">
                        <Link
                          to={{
                            pathname: "/AdminMain/EditQualification",
                            state: { qualification: q },
                          }}
                          className="sp-btn sp-btn--ghost sp-btn--xs"
                          title="Edit qualification"
                        >
                          <FaPencilAlt aria-hidden />
                          Edit
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="sp-empty-inline">No qualifications on file.</p>
              )}
            </SectionCard>
          </div>
        )}
      </div>

      {showAddQualification ? (
        <div
          className="sp-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Add qualification"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeAddQualification();
          }}
        >
          <div className="sp-modal">
            <div className="sp-modal__head">
              <h3 className="sp-modal__title">Add Qualification</h3>
              <button
                type="button"
                className="sp-btn sp-btn--ghost sp-btn--sm"
                onClick={closeAddQualification}
              >
                <FaTimes aria-hidden />
                Close
              </button>
            </div>

            <div className="sp-modal__body">
              <div className="sp-grid">
                <div className="sp-form-field">
                  <label className="sp-form-field__label">Degree / Diploma</label>
                  <input
                    type="text"
                    value={qDegree}
                    onChange={(e) => setQDegree(e.target.value)}
                    className="sp-input"
                    placeholder="e.g. B.Ed"
                  />
                </div>
                <div className="sp-form-field">
                  <label className="sp-form-field__label">
                    University / Institute
                  </label>
                  <input
                    type="text"
                    value={qUniversity}
                    onChange={(e) => setQUniversity(e.target.value)}
                    className="sp-input"
                    placeholder="Institute name"
                  />
                </div>
                <div className="sp-form-field">
                  <label className="sp-form-field__label">Passing Year</label>
                  <input
                    type="text"
                    value={qYear}
                    onChange={(e) => setQYear(e.target.value)}
                    className="sp-input"
                    placeholder="e.g. 2020"
                  />
                </div>
                <div className="sp-form-field">
                  <label className="sp-form-field__label">Percentage</label>
                  <input
                    type="text"
                    value={qPercentage}
                    onChange={(e) => setQPercentage(e.target.value)}
                    className="sp-input"
                    placeholder="e.g. 75%"
                  />
                </div>
              </div>
            </div>

            <div className="sp-modal__foot">
              <button
                type="button"
                className="sp-btn sp-btn--ghost"
                onClick={resetQualificationForm}
              >
                Reset
              </button>
              <button
                type="button"
                className="sp-btn sp-btn--primary"
                onClick={submitQualification}
                disabled={busy}
              >
                <FaCheck aria-hidden />
                {busy ? "Saving…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <Router>
      <Switch>
        <Route path="/AdminMain/StaffProfile/:Staff_id" exact>
          {profileView}
        </Route>
        <Route path="/AdminMain/EditStaff" component={EditStaff} />
        <Route path="/AdminMain/AddQualification" component={AddQualification} />
        <Route
          path="/AdminMain/EditQualification"
          component={EditQualification}
        />
      </Switch>
    </Router>
  );
};

export default StaffProfile;
